import { Leaf, Shield, Users } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import AboutKyleProphilePhoto from '../assets/AboutKyleProphilePhoto.webp';
import AboutRossellaProfilePhoto from '../assets/AboutRossellaProfilePhoto.webp';
import FamilyPic from '../assets/familypic.webp';

export default function OurStory() {
	const { t } = useTranslation();

	return (
		<div
			id='story-section'
			className='max-w-7xl my-10 mx-auto px-4 sm:px-6 lg:px-8 space-y-16 animate-fade-in text-left'
		>
			{/* Magazine Cover Intro */}
			<div className='grid grid-cols-1 lg:grid-cols-12 gap-12 items-center'>
				<div className='lg:col-span-7 space-y-6'>
					{/* <span className='font-sans text-[11px] tracking-[0.3em] text-brand-gold-dark font-bold uppercase block'>
						{t('aboutUsHeader')}
					</span> */}
					<h1 className='font-serif text-3xl sm:text-5xl text-brand-forest leading-[1.12]! tracking-tight font-medium'>
						{t('aboutUsTitlePart1')} <span className='italic text-brand-gold font-light'>{t('aboutUsTitlePart2')}</span>
					</h1>
					<p className='font-sans text-xs sm:text-sm text-brand-charcoal-light font-light leading-relaxed max-w-2xl'>
						{t('aboutParagraph1')}
					</p>
					<p className='font-sans text-xs sm:text-sm text-brand-charcoal-light font-light leading-relaxed max-w-2xl'>
						{t('aboutParagraph2')}
					</p>

					<div className='grid grid-cols-2 md:grid-cols-3 gap-6 pt-2'>
						<div>
							<span className='font-serif text-2xl sm:text-3.5xl text-brand-forest block font-bold leading-none'>
								2025
							</span>
							<span className='font-sans text-[10px] text-brand-charcoal-light uppercase tracking-widest block mt-1'>
								{t('foundingYearLabel')}
							</span>
						</div>
						<div className='flex flex-col justify-center sm:items-center'>
							<span className='font-serif text-2xl sm:text-3.5xl text-brand-forest block font-bold leading-none'>
								100%
							</span>
							<span className='font-sans text-[10px] text-brand-charcoal-light uppercase tracking-widest block mt-1'>
								{t('ethicalLabel')}
							</span>
						</div>
						<div className='flex flex-col justify-center sm:items-end'>
							<span className='font-serif text-xl sm:text-3.5xl text-brand-forest block font-bold leading-none'>
								{t('personalLabel')}
							</span>
							<span className='font-sans text-[10px] text-brand-charcoal-light uppercase tracking-widest block mt-1'>
								{t('personalLabelDesc')}
							</span>
						</div>
					</div>
				</div>

				<div className='lg:col-span-5 relative mt-6 lg:mt-0'>
					<div className='absolute -inset-2 rounded-2xl bg-brand-gold/10 -rotate-2'></div>
					<div className='relative rounded-xl overflow-hidden shadow-xl aspect-4/3 border border-earth-sand/30 bg-white'>
						<img
							src={FamilyPic} //https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=800&q=80
							alt='Majestic Elephant matriarch leading herd against sunset'
							loading='lazy'
							decoding='async'
							className='w-full h-full object-cover'
							referrerPolicy='no-referrer'
						/>
						{/* <div className='absolute inset-0 bg-linear-to-t from-brand-forest-dark/50 to-transparent'></div>
						<div className='absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-md p-3.5 rounded-lg border border-earth-sand/20 text-left'>
							<span className='font-serif text-[11px] text-brand-forest font-bold block'>The Amboseli Matriarchs</span>
							<span className='font-sans text-[9px] text-brand-charcoal-light mt-0.5 block'>
								Photographed on field ranger patrol, April 2026.
							</span>
						</div> */}
					</div>
				</div>
			</div>

			{/* Editorial: Meet the Visionaries */}
			<section id='founders-journal' className='pt-12 border-t border-earth-sand/20 space-y-10'>
				{/* Visual Title Block */}
				<div className='max-w-3xl text-left space-y-4'>
					{/* <span className='font-mono text-[9px] tracking-[0.3em] text-brand-gold-dark font-extrabold uppercase block'>
						{t('foundersHeader')}
					</span> */}
					<h2 className='font-serif text-3xl sm:text-4.5xl text-brand-forest font-bold tracking-tight'>
						{t('foundersTitle')}
					</h2>
					<div className='w-12 h-0.5 bg-brand-gold'></div>
					<p className='font-sans text-xs sm:text-[13px] text-brand-charcoal-light font-light leading-relaxed'>
						{t('foundersTitleDesc')}
					</p>
				</div>

				{/* Double Card Grid */}
				<div className='grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch'>
					{/* Kyle Blewitt Card */}
					<div className='bg-white border border-earth-sand/30 rounded-2xl overflow-hidden shadow-xs flex flex-col'>
						<div className='relative h-72 sm:h-80 bg-earth-cream shrink-0'>
							<img
								src={AboutKyleProphilePhoto}
								alt='Kyle Blewitt - Founder of Elephant Expeditions'
								loading='lazy'
								decoding='async'
								className='w-full h-full object-cover'
								referrerPolicy='no-referrer'
							/>
							<div className='absolute inset-0 bg-linear-to-t from-brand-forest-dark/40 to-transparent'></div>
							{/* <div className='absolute bottom-3 left-3 bg-brand-forest-dark/95 backdrop-blur-xs px-2.5 py-1 rounded text-white font-mono text-[8px] tracking-widest uppercase font-bold border border-white/10'>
								Co-Founder / Aviator & Photographer
							</div> */}
						</div>

						<div className='p-6 sm:p-8 flex flex-col flex-1 justify-between text-left space-y-4'>
							<div className='space-y-3'>
								<span className='font-sans text-[10px] tracking-widest text-brand-gold-dark font-extrabold uppercase block italic'>
									{t('founderKyleTitle')}
								</span>
								<h3 className='font-serif text-xl sm:text-2.5xl text-brand-forest font-bold'>Kyle Blewitt</h3>
								<p className='font-sans text-xs text-brand-charcoal-light font-light leading-relaxed'>
									{t('founderKyleBio')}
									{/* Kyle Blewitt’s journey reads like an adventure novel. A pilot since he was 15, Kyle has spent years
									flying over some of the world’s most breathtaking landscapes, from Africa’s vast Savannah to India’s
									rugged terrains and the pristine waters of the Seychelles. His work has ranged from flying for medivac
									missions and cargo runs in remote regions to piloting private jets across Europe, North Africa, and
									Asia. Yet, no matter where he roams, his heart is always drawn back to the African bush. */}
								</p>
								<p className='font-sans text-xs text-brand-charcoal-light font-light leading-relaxed'>
									{t('founderKyleBio2')}
									{/* Having grown up in South Africa, Kyle was immersed in the wilderness from a young age, where he
									developed an intense respect for Africa’s animals and ecosystems. He spent his childhood immersed in
									spearfishing, fishing, and discovering the rich intricacies of Africa’s fauna and flora. Over time, he
									also honed his skills as a wildlife photographer, capturing moments in the wild that reveal his deep
									connection to the land and its creatures. */}
								</p>
								<p className='font-sans text-xs text-brand-charcoal-light font-light leading-relaxed'>
									{t('founderKyleBio3')}
									{/* Now living in Switzerland, Kyle felt the pull to share his love for African landscapes, its untamed
									beauty, and its diverse wildlife with others. Founding Elephant Expeditions was a way for him to “come
									home” to Africa while giving travellers the opportunity to experience the magic he’s always known. */}
								</p>
							</div>
						</div>
					</div>

					{/* Rossella Blewitt Card */}
					<div className='bg-white border border-earth-sand/30 rounded-2xl overflow-hidden shadow-xs flex flex-col'>
						<div className='relative h-72 sm:h-80 bg-earth-cream shrink-0'>
							<img
								src={AboutRossellaProfilePhoto}
								alt='Rossella Blewitt next to baby hippo - Co-Founder of Elephant Expeditions'
								loading='lazy'
								decoding='async'
								className='w-full h-full object-cover'
								style={{ objectPosition: 'center 18%' }}
								referrerPolicy='no-referrer'
							/>
							<div className='absolute inset-0 bg-linear-to-t from-brand-forest-dark/40 to-transparent'></div>
							{/* <div className='absolute bottom-3 left-3 bg-brand-forest-dark/95 backdrop-blur-xs px-2.5 py-1 rounded text-white font-mono text-[8px] tracking-widest uppercase font-bold border border-white/10'>
								Co-Founder / Educator & Conservationist
							</div> */}
						</div>

						<div className='p-6 sm:p-8 flex flex-col flex-1 justify-between text-left space-y-4'>
							<div className='space-y-3'>
								<span className='font-sans text-[10px] tracking-widest text-brand-gold-dark font-extrabold uppercase block italic'>
									{t('founderRossellaTitle')}
								</span>
								<h3 className='font-serif text-xl sm:text-2.5xl text-brand-forest font-bold'>Rossella Blewitt</h3>
								<p className='font-sans text-xs text-brand-charcoal-light font-light leading-relaxed'>
									{t('founderRossellaBio')}
									{/* Rossella Blewitt’s journey is one of exploration, education, and a deep passion for animals. Born and
									raised in Switzerland, she is an English teacher for the Canton of Ticino. Her academic pursuits took
									her to the UK, Iceland, and Italy, alongside extensive travels around the world broadening her
									outlook. */}
								</p>
								<p className='font-sans text-xs text-brand-charcoal-light font-light leading-relaxed'>
									{t('founderRossellaBio2')}
									{/* Her life took an unexpected and beautiful turn when she applied for a volunteer program with Wildlife
									ACT, where she helped research and track endangered species in South Africa. It was there that she met
									Kyle, her now-husband and cofounder of Elephant Expeditions, and fell in love not just with him, but
									with the continent of Africa. */}
								</p>
								<p className='font-sans text-xs text-brand-charcoal-light font-light leading-relaxed'>
									{t('founderRossellaBio3')}
									{/* Since then, Rossella’s connection to Africa has only deepened. She had the unique experience of
									tracking pangolins through Big 5 reserves, and she continues to travel across South Africa, taking
									courses to expand her knowledge. Her passion for the African wildlife and its conservation drives her
									to continuously learn about the continent's flora and fauna. */}
								</p>
								<p className='font-sans text-xs text-brand-charcoal-light font-light leading-relaxed'>
									{t('founderRossellaBio4')}
									{/* Rossella's blend of educational knowledge and hands-on bush experience enables her to connect with
									travelers, offering them a genuine perspective on Africa's wildlife and culture. Together with Kyle,
									she brings passion, knowledge, and dedication to Elephant Expeditions, creating unforgettable safari
									experiences. */}
								</p>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Narrative Pillars Grid */}
			<section className='bg-white rounded-2xl border border-earth-sand/30 p-8 sm:p-12 space-y-10'>
				<div className='text-center max-w-2xl mx-auto space-y-2'>
					{/* <span className='font-mono text-[9px] tracking-widest text-brand-gold uppercase font-bold'>
						{t('safeguardsHeader')}
					</span> */}
					<h2 className='font-serif text-2xl sm:text-3xl text-brand-forest font-semibold tracking-tight'>
						{t('coreCommitmentsTitle')}
					</h2>
					<p className='font-sans text-xs text-brand-charcoal-light font-light leading-relaxed'>
						{t('coreCommitmentsSubtitle')}
					</p>
				</div>

				<div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
					{/* Pillar 1 */}
					<div className='space-y-3.5 border-t border-earth-sand/30 pt-6'>
						<div className='bg-brand-forest/10 w-10 h-10 rounded-full flex items-center justify-center text-brand-forest'>
							<Shield className='w-5 h-5 text-brand-forest' />
						</div>
						<h3 className='font-serif text-base text-brand-forest font-bold'>{t('pillarAntiPoachingTitle')}</h3>
						<p className='font-sans text-xs text-brand-charcoal-light font-light leading-relaxed'>
							{t('pillarAntiPoachingDesc')}
						</p>
					</div>

					{/* Pillar 2 */}
					<div className='space-y-3.5 border-t border-earth-sand/30 pt-6'>
						<div className='bg-brand-forest/10 w-10 h-10 rounded-full flex items-center justify-center text-brand-forest'>
							<Leaf className='w-5 h-5 text-brand-forest' />
						</div>
						<h3 className='font-serif text-base text-brand-forest font-bold'>{t('pillarCommunityTitle')}</h3>
						<p className='font-sans text-xs text-brand-charcoal-light font-light leading-relaxed'>
							{t('pillarCommunityDesc')}
						</p>
					</div>

					{/* Pillar 3 */}
					<div className='space-y-3.5 border-t border-earth-sand/30 pt-6'>
						<div className='bg-brand-forest/10 w-10 h-10 rounded-full flex items-center justify-center text-brand-forest'>
							<Users className='w-5 h-5 text-brand-forest' />
						</div>
						<h3 className='font-serif text-base text-brand-forest font-bold'>{t('pillarSovereignTrustTitle')}</h3>
						<p className='font-sans text-xs text-brand-charcoal-light font-light leading-relaxed'>
							{t('pillarSovereignTrustDesc')}
						</p>
					</div>
				</div>
			</section>
		</div>
	);
}
